/**
 * BranchesPage — Org Structure · Branches.
 *
 * Reads /v3/config/branches + /v3/config/regions (for the region picker).
 * Full CRUD via the keyless admin reference surface:
 *   - Save:   POST /v3/admin/branches  (client.retail.admin.reference.branches.upsert)
 *   - Delete: SOFT (upsert isActive=false) — matches legacy procedure + ROP-teams
 *             behavior. The hard DELETE route is deliberately NOT used: every
 *             account links a branchCode, and FK constraints (Users / branch
 *             aliases / overrides / operating-hours) make a hard delete either
 *             blocked or silently destructive.
 *
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer carries authority; no bypass-key
 * secret in the bundle). Migrated from ROP-teams Admin (BACKLOG-0579 Batch 2).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL, ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

// Local row — the /v3/config/branches wire returns fax / timeZone / hasSdb /
// isKioskEnabled that the shared-types Branch read type doesn't surface
// (V3ConfigFunction projects them). Read by casting, the same way the retired
// ROP-teams editor did.
interface BranchRow {
  branchCode: string;
  branchName: string;
  regionCode: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  fax: string | null;
  timeZone: string | null;
  isKioskEnabled: boolean;
  hasSdb: boolean;
  isActive: boolean;
  entrustUrl: string | null;
}

interface RegionLite {
  regionCode: string;
  regionName: string;
}

interface BranchDraft {
  isNew: boolean;
  lockedKey: boolean;
  branchCode: string;
  branchName: string;
  regionCode: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  fax: string;
  timeZone: string;
  isKioskEnabled: boolean;
  hasSdb: boolean;
  isActive: boolean;
  entrustUrl: string;
}

// Common US bank timezones. The entity defaults America/Chicago; the column is
// free-form so this is a UX guardrail, not a hard constraint.
const COMMON_TIMEZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'America/Chicago', label: 'America/Chicago (Central)' },
  { value: 'America/New_York', label: 'America/New_York (Eastern)' },
  { value: 'America/Denver', label: 'America/Denver (Mountain)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific)' },
  { value: 'America/Phoenix', label: 'America/Phoenix (Arizona, no DST)' },
  { value: 'America/Anchorage', label: 'America/Anchorage (Alaska)' },
  { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu (Hawaii)' },
];

function emptyDraft(defaultRegion: string): BranchDraft {
  return {
    isNew: true,
    lockedKey: false,
    branchCode: '',
    branchName: '',
    regionCode: defaultRegion,
    addressLine1: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    fax: '',
    timeZone: 'America/Chicago',
    isKioskEnabled: false,
    hasSdb: false,
    isActive: true,
    entrustUrl: '',
  };
}

function rowToDraft(b: BranchRow): BranchDraft {
  return {
    isNew: false,
    lockedKey: true,
    branchCode: b.branchCode,
    branchName: b.branchName,
    regionCode: b.regionCode ?? '',
    addressLine1: b.addressLine1 ?? '',
    city: b.city ?? '',
    state: b.state ?? '',
    zip: b.zip ?? '',
    phone: b.phone ?? '',
    fax: b.fax ?? '',
    timeZone: b.timeZone ?? 'America/Chicago',
    isKioskEnabled: b.isKioskEnabled,
    hasSdb: b.hasSdb,
    isActive: b.isActive,
    entrustUrl: b.entrustUrl ?? '',
  };
}

// Build the BranchUpsertBody. `forceInactive` powers the soft-delete path.
function draftToBody(d: BranchDraft, forceInactive = false) {
  return {
    branchCode: d.branchCode.trim(),
    branchName: d.branchName.trim(),
    regionCode: d.regionCode.trim(),
    addressLine1: d.addressLine1.trim() || null,
    city: d.city.trim() || null,
    state: d.state.trim() || null,
    zip: d.zip.trim() || null,
    phone: d.phone.trim() || null,
    fax: d.fax.trim() || null,
    timeZone: d.timeZone.trim() || null,
    isKioskEnabled: d.isKioskEnabled,
    hasSdb: d.hasSdb,
    isActive: forceInactive ? false : d.isActive,
    entrustUrl: d.entrustUrl.trim() || null,
  };
}

export function BranchesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<BranchRow> | null>(null);
  const [regions, setRegions] = useState<ReadonlyArray<RegionLite>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [syncing, setSyncing] = useState(false);
  const [syncBanner, setSyncBanner] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Edit/Add modal state.
  const [draft, setDraft] = useState<BranchDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setError(null);
      try {
        // Load branches + regions together — the region picker needs the live
        // region list (UpsertBranch does NOT validate the region FK server-side,
        // so the UI must offer a dropdown, never free text).
        const [bResp, rResp] = await Promise.all([
          client.retail.config.branches(),
          client.retail.config.regions(),
        ]);
        if (cancelled) return;
        if (bResp.success && Array.isArray(bResp.data)) {
          setRows(bResp.data as unknown as ReadonlyArray<BranchRow>);
        } else {
          setError(bResp.error ?? 'Failed to load branches.');
        }
        if (rResp.success && Array.isArray(rResp.data)) {
          setRegions(
            (rResp.data as unknown as ReadonlyArray<{ regionCode: string; regionName: string }>)
              .map((r) => ({ regionCode: r.regionCode, regionName: r.regionName }))
              .filter((r) => r.regionCode),
          );
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  // v0.9.2 — sync from Core Director via FinInstInfoInq + auto-map Entra
  // officeLocation values to branch EntraLocationCode.
  const onCdSync = useCallback(async () => {
    setSyncing(true);
    setSyncBanner(null);
    try {
      const resp = await client.retail.admin.branchesCdSync();
      if (resp.success && resp.data) {
        const d = resp.data;
        setSyncBanner(
          `CD sync: ${d.cdRecordCount} CD branches · inserted ${d.inserted} · renamed ${d.renamed} · ` +
            `Entra locations mapped this run: ${d.entraMappedCount}` +
            (d.cdNotInV3 > 0 ? ` · ${d.cdNotInV3} ROP branch(es) not in CD` : ''),
        );
      } else {
        setSyncBanner(`CD sync failed: ${resp.error ?? resp.code ?? 'unknown'}`);
      }
    } catch (e) {
      setSyncBanner(`CD sync failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [client, refresh]);

  const regionOptions = useMemo(
    () => regions.map((r) => ({ value: r.regionCode, label: `${r.regionCode} — ${r.regionName}` })),
    [regions],
  );
  const regionNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of regions) m.set(r.regionCode, r.regionName);
    return m;
  }, [regions]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter(
      (b) =>
        b.branchCode.toLowerCase().includes(n) ||
        b.branchName.toLowerCase().includes(n) ||
        (b.city ?? '').toLowerCase().includes(n) ||
        (b.regionCode ?? '').toLowerCase().includes(n),
    );
  }, [rows, filter]);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.branchCode.trim()) {
      setSaveError('Branch code is required.');
      return;
    }
    if (draft.branchCode.trim().length > 8) {
      setSaveError('Branch code must be 8 characters or fewer.');
      return;
    }
    if (!draft.branchName.trim()) {
      setSaveError('Branch name is required.');
      return;
    }
    if (!draft.regionCode.trim()) {
      setSaveError('Region is required — pick one from the list.');
      return;
    }
    // Entrust URL is optional (deferred until Collette supplies it), but if the
    // admin types something it must be a real absolute http(s) URL — the Card
    // Issuance panel hands it straight to Teams app.openLink.
    const entrust = draft.entrustUrl.trim();
    if (entrust && !/^https?:\/\/.+/i.test(entrust)) {
      setSaveError('Entrust launch URL must start with http:// or https:// (or leave it blank).');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.reference.branches.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created branch "${draft.branchCode}".`
          : `Updated branch "${draft.branchCode}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  // Soft delete — deactivate via upsert(isActive=false). The hard DELETE route
  // is intentionally avoided (FK-restricted / cascade-destructive).
  const handleDeactivate = useCallback(async () => {
    if (!draft || draft.isNew) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.reference.branches.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToBody(draft, true),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'deactivate returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'warn',
        text: `Deactivated branch "${draft.branchCode}". History preserved; it no longer appears as selectable.`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<BranchRow>> = useMemo(
    () => [
      { header: 'Code', cell: (b) => <code className="cell-mono--bold">{b.branchCode}</code> },
      { header: 'Name', cell: (b) => b.branchName },
      {
        header: 'Region',
        cell: (b) =>
          b.regionCode ? (
            <code className="cell-mono">{b.regionCode}</code>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'City / State',
        cell: (b) =>
          b.city || b.state ? (
            `${b.city ?? ''}${b.city && b.state ? ', ' : ''}${b.state ?? ''}`
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Phone',
        cell: (b) =>
          b.phone ? <span className="cell-mono">{b.phone}</span> : <span className="cell-dim">—</span>,
      },
      {
        header: 'Status',
        cell: (b) =>
          b.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: 'Entrust',
        cell: (b) =>
          b.entrustUrl && b.entrustUrl.trim() ? (
            <Pill tone="ok">linked</Pill>
          ) : (
            <Pill tone="muted">not set</Pill>
          ),
      },
      { header: '', cell: (b) => <Button onClick={() => { setSaveError(null); setDraft(rowToDraft(b)); }}>Edit</Button> },
    ],
    [],
  );

  if (error) return <Banner tone="err">Could not load branches: {error}</Banner>;
  if (!rows) return <Spinner label="Loading branches…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Branches — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="Filter by code, name, city, or region…"
              />
            </div>
            <Button onClick={() => void onCdSync()} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync from CD'}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setSaveError(null);
                setDraft(emptyDraft(regions[0]?.regionCode ?? ''));
              }}
              disabled={regions.length === 0}
            >
              + Add Branch
            </Button>
          </div>
        }
      >
        {syncBanner && <Banner tone="info">{syncBanner}</Banner>}
        <DataTable<BranchRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(b) => b.branchCode}
          emptyState={<p className="empty">No branches match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add Branch' : `Edit branch ${draft?.branchCode ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={640}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              {!draft.isNew && draft.isActive && (
                <Button onClick={() => void handleDeactivate()} disabled={saving}>
                  Deactivate
                </Button>
              )}
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Branch code"
              value={draft.branchCode}
              onChange={(v) => setDraft({ ...draft, branchCode: v.slice(0, 8) })}
              placeholder="e.g. 01"
              disabled={draft.lockedKey}
              monospace
              maxLength={8}
              hint={
                draft.lockedKey
                  ? 'Branch code is permanent — deactivate and recreate to re-key.'
                  : 'Up to 8 characters. Matches the Core Director branch number.'
              }
            />

            <TextField
              label="Branch name"
              value={draft.branchName}
              onChange={(v) => setDraft({ ...draft, branchName: v })}
              placeholder="e.g. Main Street"
              hint="Display name shown across the platform."
            />

            <SelectField
              label="Region"
              value={draft.regionCode}
              onChange={(v) => setDraft({ ...draft, regionCode: v })}
              options={regionOptions}
              placeholder="— select a region —"
              hint="Required. Branches must belong to an existing region (manage regions on the Regions tab)."
            />

            <TextField
              label="Address"
              value={draft.addressLine1}
              onChange={(v) => setDraft({ ...draft, addressLine1: v })}
              placeholder="Street address"
            />

            <div className="form-row">
              <TextField
                label="City"
                value={draft.city}
                onChange={(v) => setDraft({ ...draft, city: v })}
              />
              <TextField
                label="State"
                value={draft.state}
                onChange={(v) => setDraft({ ...draft, state: v.toUpperCase().slice(0, 2) })}
                maxLength={2}
                monospace
                placeholder="MO"
              />
              <TextField
                label="ZIP"
                value={draft.zip}
                onChange={(v) => setDraft({ ...draft, zip: v })}
                monospace
              />
            </div>

            <div className="form-row">
              <TextField
                label="Phone"
                value={draft.phone}
                onChange={(v) => setDraft({ ...draft, phone: v })}
                monospace
                placeholder="+1 573 555 1234"
              />
              <TextField
                label="Fax"
                value={draft.fax}
                onChange={(v) => setDraft({ ...draft, fax: v })}
                monospace
              />
            </div>

            <SelectField
              label="Time zone"
              value={draft.timeZone}
              onChange={(v) => setDraft({ ...draft, timeZone: v })}
              options={COMMON_TIMEZONES}
              hint="Used for branch-local operating hours + scheduling."
            />

            <TextField
              label="Entrust launch URL"
              value={draft.entrustUrl}
              onChange={(v) => setDraft({ ...draft, entrustUrl: v })}
              placeholder="https://…  (this branch's Entrust card-issuance link)"
              monospace
              maxLength={500}
              hint="Branch-specific Entrust link opened by the Card Issuance panel's “Open Entrust” button. Leave blank if not yet provided — the panel falls back to manual Entrust steps. (Collette to supply the six per-branch URLs.)"
            />

            <BoolField
              label="Safe deposit boxes"
              value={draft.hasSdb}
              onChange={(v) => setDraft({ ...draft, hasSdb: v })}
              hint="Branch offers safe deposit boxes."
            />
            <BoolField
              label="Kiosk enabled"
              value={draft.isKioskEnabled}
              onChange={(v) => setDraft({ ...draft, isKioskEnabled: v })}
              hint="Self-service kiosk available at this branch."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive branches stay for history but aren't selectable on new work."
            />

            {draft.regionCode && regionNameByCode.has(draft.regionCode) && (
              <p className="form-hint">
                Region: {regionNameByCode.get(draft.regionCode)} ({draft.regionCode})
              </p>
            )}
          </div>
        )}
      </ModalShell>
    </>
  );
}
